import { useMemo, useState } from "react"
import { formatDistanceToNow } from "date-fns"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/modules/shared/ui/components/dialog"
import { Button } from "@/modules/shared/ui/components/button"
import { Badge } from "@/modules/shared/ui/components/badge"
import { Loader2, Info, MapPin, Building2 } from "lucide-react"
import {
  useVacancySuggestionShortlist,
  useCompareVacancy,
  useVacancySuggestions,
} from "../../hooks/useVacancySuggestions"
import type { VacancyShortlistItemDto } from "../../dtos/vacancy-suggestion.dto"
import type { VacancySuggestionDto } from "../../dtos/vacancy-suggestion.dto"

const ACTIVE_SCORED_STATUSES = new Set(["Compared", "Suggested", "Accepted"])
const INACTIVE_STATUSES = new Set(["Withdrawn", "Rejected", "Expired"])

interface Props {
  open: boolean
  onClose: () => void
  sourceApplicationId: string
  candidateId: string
  onCompareComplete: (suggestion: VacancySuggestionDto) => void
}

export function CompareVacancyDialog({
  open,
  onClose,
  sourceApplicationId,
  candidateId,
  onCompareComplete,
}: Props) {
  const [selectedVacancyId, setSelectedVacancyId] = useState<string | null>(
    null,
  )

  const { data: shortlistData, isLoading: isLoadingShortlist } =
    useVacancySuggestionShortlist(open ? sourceApplicationId : undefined)

  const { data: candidateSuggestions } = useVacancySuggestions({ candidateId })

  const compareMutation = useCompareVacancy()

  const items: VacancyShortlistItemDto[] = shortlistData?.items ?? []

  const latestSuggestionByVacancyId = useMemo(() => {
    const map = new Map<string, VacancySuggestionDto>()
    for (const s of candidateSuggestions ?? []) {
      if (!map.has(s.targetVacancyId)) map.set(s.targetVacancyId, s)
    }
    return map
  }, [candidateSuggestions])

  const selectedExisting = selectedVacancyId
    ? latestSuggestionByVacancyId.get(selectedVacancyId)
    : undefined
  const selectedIsActiveScored =
    !!selectedExisting &&
    ACTIVE_SCORED_STATUSES.has(selectedExisting.status) &&
    selectedExisting.comparisonScoringStatus === "Scored"

  const handleCompare = async () => {
    if (!selectedVacancyId) return
    const result = await compareMutation.mutateAsync({
      sourceApplicationId,
      targetVacancyId: selectedVacancyId,
    })
    onCompareComplete(result)
    onClose()
    setSelectedVacancyId(null)
  }

  const handleViewExisting = () => {
    if (!selectedExisting) return
    onCompareComplete(selectedExisting)
    onClose()
    setSelectedVacancyId(null)
  }

  const handleClose = () => {
    setSelectedVacancyId(null)
    onClose()
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) handleClose()
      }}
    >
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Compare with another vacancy</DialogTitle>
          <DialogDescription>
            Select a vacancy to compare this candidate against using their
            stored CV.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          <Info className="w-4 h-4 mt-0.5 shrink-0" />
          <span>
            Running a full comparison uses AI credits from your organisation
            wallet.
          </span>
        </div>

        {isLoadingShortlist ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
            <span className="ml-2 text-muted-foreground">
              Loading vacancies…
            </span>
          </div>
        ) : items.length === 0 ? (
          <p className="py-6 text-center text-muted-foreground text-sm">
            No other open vacancies found for this organisation.
          </p>
        ) : (
          <div className="max-h-80 overflow-y-auto space-y-2 pr-1">
            {items.map((item) => {
              const existing = latestSuggestionByVacancyId.get(item.vacancyId)
              const isPending = existing?.comparisonScoringStatus === "Pending"
              const isActiveScored =
                !!existing &&
                ACTIVE_SCORED_STATUSES.has(existing.status) &&
                existing.comparisonScoringStatus === "Scored"
              const isInactive =
                !!existing && INACTIVE_STATUSES.has(existing.status)

              return (
                <button
                  key={item.vacancyId}
                  type="button"
                  disabled={isPending}
                  onClick={() => setSelectedVacancyId(item.vacancyId)}
                  className={`w-full text-left rounded-lg border px-4 py-3 transition-colors ${
                    isPending
                      ? "cursor-not-allowed opacity-60 border-border bg-card"
                      : "hover:bg-muted/50"
                  } ${
                    selectedVacancyId === item.vacancyId
                      ? "border-primary bg-primary/5"
                      : "border-border bg-card"
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-medium text-foreground truncate">
                        {item.title}
                      </p>
                      <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                        {item.department && (
                          <span className="flex items-center gap-1">
                            <Building2 className="w-3 h-3" />
                            {item.department}
                          </span>
                        )}
                        {item.location && (
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {item.location}
                          </span>
                        )}
                      </div>
                    </div>
                    {isPending && (
                      <Badge
                        variant="outline"
                        className="shrink-0 text-blue-700 bg-blue-50 border-blue-200"
                      >
                        Scoring in progress…
                      </Badge>
                    )}
                    {isActiveScored && existing?.comparisonScoredAt && (
                      <Badge
                        variant="outline"
                        className="shrink-0 text-muted-foreground"
                      >
                        Compared{" "}
                        {formatDistanceToNow(
                          new Date(existing.comparisonScoredAt),
                          { addSuffix: true },
                        )}
                      </Badge>
                    )}
                    {isInactive && (
                      <Badge
                        variant="outline"
                        className="shrink-0 text-muted-foreground"
                      >
                        Previously compared
                      </Badge>
                    )}
                  </div>
                </button>
              )
            })}
          </div>
        )}

        <div className="flex justify-end gap-3 pt-2">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={compareMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={
              selectedIsActiveScored ? handleViewExisting : handleCompare
            }
            disabled={!selectedVacancyId || compareMutation.isPending}
          >
            {compareMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Comparing…
              </>
            ) : selectedIsActiveScored ? (
              "View existing comparison"
            ) : (
              "Run comparison"
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
